import { useMutation, useApolloClient } from '@apollo/react-hooks';
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  EXPORT_CUSTOMERS_V3,
  CUSTOMER_EXPORT_STATUS,
  GET_EXPORT_DOWNLOAD_URL,
} from '../gql/exportCustomers';

const useCustomerExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const subscriptionRef = useRef(null);

  const client = useApolloClient();

  // Export mutation
  const [exportCustomersMutation] = useMutation(EXPORT_CUSTOMERS_V3);

  // Start export function
  const startExport = useCallback(
    async (accountId, filters = {}, format = 'CSV') => {
      try {
        setIsExporting(true);

        // Get user's timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Build the input object
        const input = {
          account_id: accountId,
          limit: null, // Export all matching customers
          offset: null,
          filters: {},
          format: format.toUpperCase(), // Add format inside input
          timezone: timezone, // Add user's timezone
        };

        // Add filters if they exist
        if (filters.search) {
          input.filters.search = filters.search;
        }
        if (filters.segment) {
          input.filters.segment = filters.segment;
        }
        if (filters.minValue) {
          input.filters.minValue = filters.minValue;
        }
        if (filters.maxValue) {
          input.filters.maxValue = filters.maxValue;
        }
        if (filters.attendanceRate) {
          input.filters.attendanceRate = filters.attendanceRate;
        }
        if (filters.locationCategory) {
          input.filters.locationCategory = filters.locationCategory;
        }
        if (filters.dateFrom) {
          input.filters.dateFrom = filters.dateFrom;
        }
        if (filters.dateTo) {
          input.filters.dateTo = filters.dateTo;
        }

        // Execute the mutation
        const { data } = await exportCustomersMutation({
          variables: {
            input,
          },
        });

        if (data?.exportCustomersV3?.jobId) {
          setCurrentJobId(data.exportCustomersV3.jobId);
          return data.exportCustomersV3.jobId;
        } else {
          throw new Error('No job ID returned from export');
        }
      } catch (error) {
        setIsExporting(false);
        throw error;
      }
    },
    [exportCustomersMutation]
  );

  // Subscribe to export progress
  const subscribeToProgress = useCallback(
    (jobId, callbacks = {}) => {
      const { onComplete, onError, onProgress } = callbacks;

      // Clean up any existing subscription
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }

      // Create new subscription
      subscriptionRef.current = client
        .subscribe({
          query: CUSTOMER_EXPORT_STATUS,
          variables: { jobId },
        })
        .subscribe({
          next: ({ data }) => {
            const status = data?.customerExportStatus;

            if (!status) return;

            // Call progress callback if provided
            if (onProgress && status.progress !== null) {
              onProgress(status);
            }

            // Handle completion
            if (status.status === 'COMPLETED') {
              setIsExporting(false);
              if (onComplete) {
                onComplete(status);
              }
              // Clean up subscription after completion
              if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
              }
            }

            // Handle failure
            if (status.status === 'FAILED') {
              setIsExporting(false);
              if (onError) {
                onError(new Error(status.error || 'Export failed'));
              }
              // Clean up subscription after failure
              if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
              }
            }
          },
          error: (error) => {
            setIsExporting(false);
            if (onError) {
              onError(error);
            }
            // Clean up subscription on error
            if (subscriptionRef.current) {
              subscriptionRef.current.unsubscribe();
              subscriptionRef.current = null;
            }
          },
        });

      return subscriptionRef.current;
    },
    [client]
  );

  // Get download URL
  const getDownloadUrl = useCallback(
    async (jobId) => {
      try {
        const { data } = await client.query({
          query: GET_EXPORT_DOWNLOAD_URL,
          variables: { jobId: jobId || currentJobId },
          fetchPolicy: 'network-only', // Always get fresh URL
        });

        return data?.getExportDownloadUrl;
      } catch (error) {
        console.error('Failed to get download URL:', error);
        throw error;
      }
    },
    [client, currentJobId]
  );

  // Clean up subscription on unmount
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, []);

  return {
    startExport,
    subscribeToProgress,
    getDownloadUrl,
    isExporting,
    currentJobId,
  };
};

export default useCustomerExport;
